import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Breadcrumbs,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Grid,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  EditNote as EditNoteIcon,
  AddAPhoto as AddAPhotoIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';

const NewProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { logout } = useAuth();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_per_kg: '',
    unit: 'kg',
    weight_per_unit: '',
    is_active: true,
  });

  const fetchProduct = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/products/${id}/`);
      const product = response.data;
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price_per_kg: product.price_per_kg || '',
        unit: product.unit || 'kg',
        weight_per_unit: product.weight_per_unit || '',
        is_active: product.is_active !== undefined ? product.is_active : true,
      });
      if (product.image_url) {
        setImagePreview(product.image_url);
      }
    } catch (err) {
      setError('Error al cargar el producto');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditing) {
      fetchProduct();
    }
  }, [isEditing, fetchProduct]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('La imagen no debe superar los 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setFormData({
        ...formData,
        image: file,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === 'image' && formData[key]) {
          data.append(key, formData[key]);
        } else if (formData[key] !== null && formData[key] !== undefined) {
          data.append(key, formData[key]);
        }
      });

      if (isEditing) {
        await axios.patch(`/api/products/${id}/`, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setSuccess('Producto actualizado exitosamente');
      } else {
        await axios.post('/api/products/', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setSuccess('Producto creado exitosamente');
      }

      setTimeout(() => {
        navigate('/products');
      }, 1000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        Object.values(err.response?.data || {})[0]?.[0] ||
        'Error al guardar producto';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const units = [
    { value: 'kg', label: 'Kilogramos' },
    { value: 'unidad', label: 'Unidades' },
    { value: 'caja', label: 'Cajas' },
    { value: 'pack', label: 'Packs' },
  ];

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar onLogout={handleLogout} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
        }}
      >
        <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 } }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              sx={{ color: '#4e7397', fontSize: '0.875rem' }}
            >
              Inicio
            </Link>
            <Link
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate('/products');
              }}
              sx={{ color: '#4e7397', fontSize: '0.875rem' }}
            >
              Productos
            </Link>
            <Typography color="text.primary" sx={{ color: '#800020', fontSize: '0.875rem', fontWeight: 500 }}>
              {isEditing ? 'Editar Producto' : 'Registro de Producto'}
            </Typography>
          </Breadcrumbs>

          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                color: '#0e141b',
                fontSize: { xs: '1.875rem', md: '2.25rem' },
                fontWeight: 900,
                mb: 1,
                letterSpacing: '-0.033em',
              }}
            >
              {isEditing ? 'Editar Producto' : 'Registro de Producto'}
            </Typography>
            <Typography sx={{ color: '#4e7397', fontSize: '1rem' }}>
              Ingrese los detalles para {isEditing ? 'editar' : 'registrar'} un producto en el catálogo.
            </Typography>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Image Upload Column */}
              <Grid item xs={12} lg={4}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: '0.75rem',
                    border: '1px solid #d0dbe7',
                    boxShadow: 'none',
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: '#0e141b', mb: 2, fontSize: '0.875rem' }}>
                    Imagen del Producto
                  </Typography>
                  <Box
                    component="label"
                    sx={{
                      display: 'block',
                      width: '100%',
                      aspectRatio: '1',
                      borderRadius: '0.75rem',
                      border: '2px dashed #d0dbe7',
                      bgcolor: '#f6f7f8',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        borderColor: '#800020',
                      },
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                    {imagePreview ? (
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Preview"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          p: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: '#ffffff',
                            border: '1px solid #d0dbe7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                          }}
                        >
                          <AddAPhotoIcon sx={{ color: '#800020', fontSize: '2rem' }} />
                        </Box>
                        <Typography sx={{ fontWeight: 500, color: '#0e141b', fontSize: '0.875rem', mb: 0.5 }}>
                          Subir imagen
                        </Typography>
                        <Typography sx={{ color: '#4e7397', fontSize: '0.75rem' }}>
                          PNG, JPG hasta 5MB
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: '0.5rem',
                    bgcolor: '#e3f2fd',
                    border: '1px solid #90caf9',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <InfoIcon sx={{ color: '#1976d2' }} />
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#0e141b', fontSize: '0.75rem', mb: 0.5 }}>
                        Recomendación
                      </Typography>
                      <Typography sx={{ color: '#0e141b', fontSize: '0.75rem', lineHeight: 1.5 }}>
                        Utilice imágenes con fondo claro o transparente para una mejor visualización en el catálogo.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Form Column */}
              <Grid item xs={12} lg={8}>
                <Paper
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: '0.75rem',
                    border: '1px solid #d0dbe7',
                    boxShadow: 'none',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <EditNoteIcon sx={{ color: '#800020' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0e141b' }}>
                      Detalles Generales
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        label="Nombre del Producto"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        fullWidth
                        placeholder="Ej: Asado de Tira"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Precio por Kilogramo"
                        name="price_per_kg"
                        type="number"
                        value={formData.price_per_kg}
                        onChange={handleChange}
                        required
                        fullWidth
                        inputProps={{ step: '0.01', min: '0' }}
                        helperText="El precio siempre es por kilogramo, independientemente de la unidad de medida"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          endAdornment: <InputAdornment position="end">ARS</InputAdornment>,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Descripción"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Describa las características del corte, recomendaciones de cocción, etc..."
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Unidad de Medida</InputLabel>
                        <Select
                          name="unit"
                          value={formData.unit}
                          onChange={handleChange}
                          label="Unidad de Medida"
                        >
                          {units.map((unit) => (
                            <MenuItem key={unit.value} value={unit.value}>
                              {unit.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {formData.unit !== 'kg' && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Peso Aproximado por Unidad (kg)"
                          name="weight_per_unit"
                          type="number"
                          value={formData.weight_per_unit}
                          onChange={handleChange}
                          fullWidth
                          required
                          inputProps={{ step: '0.001', min: '0' }}
                          helperText={`Ingrese el peso aproximado en kilogramos de una ${formData.unit === 'unidad' ? 'unidad' : formData.unit === 'caja' ? 'caja' : formData.unit === 'pack' ? 'pack' : 'unidad'}`}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                          }}
                        />
                      </Grid>
                    )}

                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  {/* Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      onClick={() => navigate('/products')}
                      sx={{
                        color: '#4e7397',
                        textTransform: 'none',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      disabled={loading}
                      sx={{
                        bgcolor: '#800020',
                        '&:hover': { bgcolor: '#600010' },
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 4,
                      }}
                    >
                      Guardar Producto
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

export default NewProduct;
